import { useTranslation } from 'react-i18next';
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import { NotificationDropdown } from "@/components/layout/NotificationDropdown";
import { ProfileMenu } from "@/components/layout/ProfileMenu";
import { Tooltip } from "@/components/ui/Tooltip";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { toggleSidebar } from "@/store/appSlice";
import type { ReactNode } from "react";

interface HeaderProps {
  /** Active module / workspace name shown on the left (e.g. Human Resources, HR). */
  moduleLabel?: string;
  /** @deprecated Use moduleLabel */
  title?: string;
  /** Left of header — app launcher, etc. */
  leading?: ReactNode;
  /** Right of header — company switcher, store selector, etc. */
  trailing?: ReactNode;
  extra?: ReactNode;
  onOpenSearch?: () => void;
}

export function Header({
  moduleLabel,
  title,
  leading,
  trailing,
  extra,
  onOpenSearch,
}: HeaderProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const masterAdmin = useAppSelector((s) => s.auth.user?.masterAdmin === true);
  const workspaceLabel = moduleLabel ?? title;

  return (
    <header className="workspace-header" role="banner">
      <div className="workspace-header-start">
        <button
          type="button"
          onClick={() => dispatch(toggleSidebar())}
          className="workspace-header-menu-btn lg:hidden"
          aria-label={t('labels.toggleSidebar')}
        >
          <MenuIcon sx={{ fontSize: 22 }} />
        </button>
        {leading}
        {workspaceLabel ? (
          <span className="workspace-header-module">{workspaceLabel}</span>
        ) : null}
        {trailing ? (
          <div className="workspace-header-context flex min-w-0 items-center gap-1.5 sm:gap-2">
            {trailing}
          </div>
        ) : null}
      </div>
      <div className="workspace-header-end">
        <div className="workspace-header-tools">
          {extra}
          {onOpenSearch && (
            <Tooltip content="Search (Ctrl+K)">
              <button
                type="button"
                className="workspace-header-ghost-btn"
                onClick={onOpenSearch}
                aria-label={t('labels.searchCtrlK')}
              >
                <SearchIcon sx={{ fontSize: 22 }} />
              </button>
            </Tooltip>
          )}
          {!masterAdmin && <NotificationDropdown variant="header" />}
        </div>
        <div className="workspace-header-account">
          <ProfileMenu variant="header" />
        </div>
      </div>
    </header>
  );
}
